import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useTranslation } from "react-i18next";
import { DataDialog } from "@app/components/atoms";
import type {
  IrregularMaintenanceItemChoiceMaster,
  MaintenanceCategoryMaster,
  MaintenanceSelectOption,
  MaintenanceTypeMaster,
} from "@app/types/maintenanceMasterData.types";
import {
  getIrregularMaintenanceItemOptionsForCategory,
  getIrregularMaintenanceItemOtherChoiceId,
  getMaintenanceCategoryCode,
  getMaintenanceTypeCode,
  isMaintenanceTypeAllChoice,
  isMaintenanceTypeOtherChoice,
} from "@app/api/maintenance/maintenance.api";
import {
  createEmptyMaintenanceItemEntry,
  getMaintenanceItemIcon,
  isFiltersMaintenanceCategory,
  isIrregularMaintenanceItemOtherChoice,
  isMaintenanceCommentWithinLimit,
  isMaintenanceItemTextFieldInvalid,
  MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT,
  shouldHideMaintenanceRefillReplaceFields,
  MAINTENANCE_SELECT_FORM_CONTROL_SX,
  MAINTENANCE_SELECT_MENU_PROPS,
  mapMaintenanceTypesToOptions,
  resolveIrregularMaintenanceItemForFiltersType,
  type MaintenanceItemEntry,
  MAINTENANCE_ACTION_RADIO_SX,
  MaintenanceItemAction,
  isMaintenanceItemsMediaTotalAtLimit,
  MAINTENANCE_ITEM_MEDIA_LIMIT,
  isScheduledMaintenanceItem,
  ManualMaintenanceItemEntry,
  REFILL_AMOUNT_MAX,
  sanitizeNumericInputValue,
  isNumericValueValid,
  normalizeNumericOnBlur,
} from "./maintenanceItemShared";
import "@app/pages/Features/MachineDetail/Components/InspectionHistory/recording.css";
import { IconWarningFormEdit } from "@app/components/icons";
import { MediaUpload } from "@app/pages/Features/GroupList/GroupDetail/Components/InspectionForm/MediaUpload/MediaUpload";
import RecordRTC from "recordrtc";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import StopIcon from "@mui/icons-material/Stop";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import { IconDeleteRed } from "@app/components/icons/IconDeleteRed/IconDeleteRed";

const ITEM_RECORDING_TARGET_ID = "create-maintenance-item";

function cloneBlobForPlayback(blob: Blob): Blob {
  const type =
    blob.type && blob.type !== "application/octet-stream"
      ? blob.type
      : "audio/wav";
  if (blob.type === type) return blob;
  return new Blob([blob], { type });
}

const ItemBlobAudio = memo(function ItemBlobAudio({
  blob,
  onRemove,
}: {
  blob: Blob;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const audioRef: RefObject<HTMLAudioElement> = useRef(null);
  const [src, setSrc] = useState("");

  useEffect(() => {
    if (!blob?.size) {
      setSrc("");
      return;
    }
    const playable = cloneBlobForPlayback(blob);
    const url = URL.createObjectURL(playable);
    setSrc(url);
    const el = audioRef.current;
    if (el) {
      el.src = url;
      el.load();
    }
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [blob]);

  if (!src) return null;

  return (
    <div style={{ marginTop: "12px", touchAction: "auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <audio ref={audioRef} controls src={src} style={{ flex: 1 }} />
        <Tooltip title={t("formAction.delete")}>
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              color: "#d32f2f",
              marginLeft: 2,
              borderRadius: "50%",
              transition: "all 0.3s ease",
              "&:hover": {
                border: "2px solid #d32f2f",
                backgroundColor: "transparent",
              },
            }}
          >
            <IconDeleteRed />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
});

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (entry: ManualMaintenanceItemEntry) => void;
  onDelete?: () => void;
  initialEntry?: ManualMaintenanceItemEntry | null;
  presetCategoryId?: string;
  categoryOptions: MaintenanceSelectOption[];
  maintenanceCategories: MaintenanceCategoryMaster[];
  maintenanceTypes: MaintenanceTypeMaster[];
  irregularMaintenanceItems: IrregularMaintenanceItemChoiceMaster[];
  masterDataLoading?: boolean;
  masterDataError?: string | null;
  otherItemsMediaCount?: number;
};

function buildEntryFromDraft(
  draft: ManualMaintenanceItemEntry,
  categoryLabel: string,
  maintenanceTypeLabel: string,
  maintenanceItemLabel: string,
): ManualMaintenanceItemEntry {
  return {
    ...draft,
    categoryLabel,
    maintenanceTypeLabel,
    maintenanceItemLabel,
  };
}

export function MaintenanceItemEditorDialog({
  open,
  onClose,
  onSave,
  onDelete,
  initialEntry,
  presetCategoryId,
  categoryOptions,
  maintenanceCategories,
  maintenanceTypes,
  irregularMaintenanceItems,
  masterDataLoading = false,
  masterDataError = null,
  otherItemsMediaCount = 0,
}: Props) {
  const { t } = useTranslation();
  const isEditMode = Boolean(initialEntry?.id);

  const [draft, setDraft] = useState<ManualMaintenanceItemEntry>(() =>
    createEmptyMaintenanceItemEntry(),
  );
  const [touched, setTouched] = useState(false);
  const [mediaPreviewNonce, setMediaPreviewNonce] = useState(0);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [openFormNoMic, setOpenFormNoMic] = useState(false);
  const [openFormDeleteRecording, setOpenFormDeleteRecording] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState<ReturnType<typeof RecordRTC> | null>(
    null,
  );
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingStartTimeRef = useRef<number | null>(null);
  const recordingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const [mediaAvailable, setMediaAvailable] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingActive, setRecordingActive] = useState(false);
  const stoppedRef = useRef(false);
  const skipNextRecordingBlobRef = useRef(false);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<ReturnType<typeof RecordRTC> | null>(null);

  const recordStartDisabled =
    !mediaAvailable ||
    isRecording ||
    isPaused ||
    !!draft.recordedBlob ||
    recordingTime >= 60;
  const showRecordingControls = mediaAvailable && !draft.recordedBlob;

  const patchDraft = useCallback(
    (patch: Partial<ManualMaintenanceItemEntry>) => {
      setDraft((prev) => ({ ...prev, ...patch }));
    },
    [],
  );

  const handleRequestDeleteRecording = useCallback(() => {
    setOpenFormDeleteRecording(true);
  }, []);

  const releaseMicrophone = useCallback(() => {
    const stopStreamTracks = (stream: MediaStream | null | undefined) => {
      stream?.getTracks?.().forEach((track) => {
        if (track.readyState === "live") {
          track.stop();
        }
      });
    };
    stopStreamTracks(mediaStreamRef.current);
    stopStreamTracks(recorderRef.current?.stream);
    mediaStreamRef.current = null;
  }, []);

  const stopRecording = useCallback(() => {
    const activeRecorder = recorderRef.current;
    if (!activeRecorder) {
      releaseMicrophone();
      return;
    }
    stoppedRef.current = true;
    setIsRecording(false);
    setIsPaused(false);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
    releaseMicrophone();
    activeRecorder.stopRecording(() => {
      window.setTimeout(() => {
        const raw = activeRecorder.getBlob() as Blob | undefined;
        const skip = skipNextRecordingBlobRef.current;
        skipNextRecordingBlobRef.current = false;
        if (!skip && raw && raw.size > 44) {
          patchDraft({ recordedBlob: cloneBlobForPlayback(raw) });
        }
        setRecorder(null);
        recorderRef.current = null;
        recordingStartTimeRef.current = null;
        setRecordingActive(false);
        setRecordingTime(0);
      }, 300);
    });
  }, [releaseMicrophone, patchDraft]);

  useEffect(() => {
    if (recordingTime >= 60 && isRecording && !stoppedRef.current) {
      stopRecording();
    }
  }, [recordingTime, isRecording, stopRecording]);

  const startRecording = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasMic = devices.some((device) => device.kind === "audioinput");
      if (!hasMic) {
        setOpenFormNoMic(true);
        return;
      }
      releaseMicrophone();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      patchDraft({ recordedBlob: null });
      setRecordingActive(true);
      setIsPaused(false);
      stoppedRef.current = false;
      const newRecorder = new RecordRTC(stream, {
        type: "audio",
        mimeType: "audio/webm",
        recorderType: RecordRTC.StereoAudioRecorder,
        numberOfAudioChannels: 1,
        desiredSampRate: 16000,
        audioBitsPerSecond: 64000,
      });
      recorderRef.current = newRecorder;
      recordingStartTimeRef.current = Date.now();
      newRecorder.startRecording();
      setRecorder(newRecorder);
      setIsRecording(true);
      setRecordingTime(0);
      recordingIntervalRef.current = setInterval(() => {
        if (recordingStartTimeRef.current) {
          const elapsed = Math.floor(
            (Date.now() - recordingStartTimeRef.current) / 1000,
          );
          setRecordingTime(Math.min(elapsed, 60));
        }
      }, 1000);
      recordingTimeoutRef.current = setTimeout(() => {
        stopRecording();
      }, 59200);
    } catch (err) {
      console.warn("Microphone access denied", err);
      releaseMicrophone();
      setMediaAvailable(false);
      setRecordingActive(false);
    }
  };

  const resetItemRecording = useCallback(() => {
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
    releaseMicrophone();
    setRecordingTime(0);
    setIsRecording(false);
    setIsPaused(false);
    setRecorder(null);
    recorderRef.current = null;
    setMediaAvailable(true);
    setRecordingActive(false);
    recordingStartTimeRef.current = null;
    stoppedRef.current = false;
  }, [releaseMicrophone]);

  const handleDeleteRecordingConfirmed = () => {
    if (isRecording || isPaused) {
      skipNextRecordingBlobRef.current = true;
      stopRecording();
    }
    releaseMicrophone();
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
    patchDraft({ recordedBlob: null });
    setRecordingTime(0);
    setOpenFormDeleteRecording(false);
    resetItemRecording();
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    if (initialEntry) {
      const categoryCode =
        initialEntry.categoryCode ||
        getMaintenanceCategoryCode(
          initialEntry.categoryId,
          maintenanceCategories,
        );
      let entry: MaintenanceItemEntry = { ...initialEntry, categoryCode };
      if (
        isFiltersMaintenanceCategory(categoryCode) &&
        entry.maintenanceTypeId &&
        !isMaintenanceTypeAllChoice(entry.maintenanceTypeId, maintenanceTypes)
      ) {
        const maintenanceTypeCode =
          entry.maintenanceTypeCode ||
          getMaintenanceTypeCode(entry.maintenanceTypeId, maintenanceTypes);
        const resolved = resolveIrregularMaintenanceItemForFiltersType(
          entry.categoryId,
          entry.maintenanceTypeId,
          irregularMaintenanceItems,
          maintenanceTypes,
        );
        if (resolved) {
          entry = {
            ...entry,
            maintenanceTypeCode,
            maintenanceItemId: resolved.id,
            maintenanceItemLabel: resolved.label,
            maintenanceItemOther: "",
          };
        } else {
          entry = { ...entry, maintenanceTypeCode };
        }
      } else if (entry.maintenanceTypeId) {
        entry = {
          ...entry,
          maintenanceTypeCode:
            entry.maintenanceTypeCode ||
            getMaintenanceTypeCode(entry.maintenanceTypeId, maintenanceTypes),
        };
      }
      setDraft(entry);
    } else {
      const categoryId = presetCategoryId ?? "";
      const categoryLabel =
        categoryOptions.find((c) => c.id === categoryId)?.label ?? "";
      const categoryCode = getMaintenanceCategoryCode(
        categoryId,
        maintenanceCategories,
      );
      setDraft(
        createEmptyMaintenanceItemEntry({
          categoryId,
          categoryCode,
          categoryLabel,
        }),
      );
    }
    setTouched(false);
  }, [
    open,
    initialEntry,
    presetCategoryId,
    categoryOptions,
    maintenanceCategories,
    irregularMaintenanceItems,
    maintenanceTypes,
  ]);

  const isFiltersCategory = isFiltersMaintenanceCategory(draft.categoryCode);
  const hideRefillReplaceFields = shouldHideMaintenanceRefillReplaceFields(
    draft.categoryCode,
  );

  const maintenanceTypeOptions = useMemo(
    () =>
      mapMaintenanceTypesToOptions(maintenanceTypes, {
        excludeAll: !isFiltersCategory,
      }),
    [maintenanceTypes, isFiltersCategory],
  );

  const maintenanceItemOptions = useMemo(() => {
    return getIrregularMaintenanceItemOptionsForCategory(
      draft.categoryId,
      irregularMaintenanceItems,
      {
        categoryCode: draft.categoryCode,
        maintenanceTypeId: draft.maintenanceTypeId,
        maintenanceTypeCode: draft.maintenanceTypeCode,
      },
    );
  }, [
    draft.categoryId,
    draft.categoryCode,
    draft.maintenanceTypeId,
    draft.maintenanceTypeCode,
    irregularMaintenanceItems,
  ]);

  const otherMaintenanceItemChoiceId = useMemo(
    () => getIrregularMaintenanceItemOtherChoiceId(irregularMaintenanceItems),
    [irregularMaintenanceItems],
  );

  const filtersAllowsItemOtherChoice =
    isMaintenanceTypeAllChoice(draft.maintenanceTypeId, maintenanceTypes) ||
    isMaintenanceTypeOtherChoice(draft.maintenanceTypeId, maintenanceTypes);

  const showOtherMaintenanceItemField =
    isIrregularMaintenanceItemOtherChoice(
      draft.maintenanceItemId,
      irregularMaintenanceItems,
    ) &&
    (!isFiltersCategory || filtersAllowsItemOtherChoice);

  const itemDropdownsDisabled = masterDataLoading || !!masterDataError;

  const categoryLabel =
    categoryOptions.find((c) => c.id === draft.categoryId)?.label ??
    draft.categoryLabel;

  const maintenanceTypeLabel =
    maintenanceTypeOptions.find((o) => o.id === draft.maintenanceTypeId)
      ?.label ?? draft.maintenanceTypeLabel;

  const maintenanceItemLabel =
    maintenanceItemOptions.find((o) => o.id === draft.maintenanceItemId)
      ?.label ?? draft.maintenanceItemLabel;

  const refillAmountVal = draft.refillAmount.trim();
  const refillAmountOverMax =
    !!refillAmountVal &&
    Number.isFinite(parseFloat(refillAmountVal)) &&
    parseFloat(refillAmountVal) > REFILL_AMOUNT_MAX;
  const refillAmountFormatInvalid =
    !!refillAmountVal &&
    !isNumericValueValid(refillAmountVal, REFILL_AMOUNT_MAX);
  const refillAmountInvalid = refillAmountOverMax || refillAmountFormatInvalid;
  const refillAmountShowError = !hideRefillReplaceFields && refillAmountInvalid;
  const refillAmountHelperText = refillAmountOverMax
    ? t("createInspection.numericMax", { max: REFILL_AMOUNT_MAX })
    : refillAmountFormatInvalid
      ? t("invalidCharacters")
      : "";

  const categoryMissing = !draft.categoryId;
  const typeMissing = isFiltersCategory && !draft.maintenanceTypeId;
  const itemMissing = !draft.maintenanceItemId;
  const otherMissing =
    showOtherMaintenanceItemField && !draft.maintenanceItemOther.trim();
  const maintenanceItemOtherError =
    showOtherMaintenanceItemField &&
    isMaintenanceItemTextFieldInvalid(draft.maintenanceItemOther);
  const actionMissing = !hideRefillReplaceFields && !draft.action;
  const commentError = isMaintenanceItemTextFieldInvalid(draft.comment);
  const totalItemsMediaCount = otherItemsMediaCount + draft.mediaFiles.length;

  const isSaveDisabled =
    categoryMissing ||
    typeMissing ||
    itemMissing ||
    otherMissing ||
    maintenanceItemOtherError ||
    actionMissing ||
    (!hideRefillReplaceFields && refillAmountInvalid) ||
    commentError ||
    totalItemsMediaCount > MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT;

  const uploadDescription = useMemo(
    () => [
      `${t("dialogReport.pleaseUploadFile")}`,
      `<span class="fs-12">${t("dialogReport.thatCanBeUploadedAre")}</span>`,
      `<span class="fs-12">${t("dialogReport.fileFormats")}</span>`,
      `<span class="fs-12">${t("dialogReport.videosCanBeUploaded")}</span>`,
    ],
    [t],
  );

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    setTouched(true);
    if (isSaveDisabled) {
      return;
    }
    onSave(
      buildEntryFromDraft(
        draft,
        categoryLabel,
        maintenanceTypeLabel,
        maintenanceItemLabel,
      ),
    );
    handleClose();
  };

  const selectRenderValue = (
    selected: string,
    options: { id: string; label: string }[],
  ) => {
    if (!selected) return "";
    return options.find((o) => o.id === selected)?.label ?? selected;
  };

  return (
    <>
      <DataDialog
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{t("dialogMaintenanceReports.item")}</span>

            <span style={{ color: "grey", fontSize: "12px", fontWeight: 500 }}>
              *{t("required")}
            </span>
          </div>
        }
        open={open}
        setOpen={(value: boolean) => {
          if (!value) {
            handleClose();
          }
        }}
        onClose={handleClose}
        fullWidth
        disableEnforceFocus
        className="dialog-container"
        dialogContentSx={{
          overflow: "auto",
          maxHeight: "min(70vh, 720px)",
          touchAction: "auto",
          pt: 2,
        }}
        dialogActions={
          <>
            <Button
              className="btn btn-no-border"
              style={{ width: "auto", marginRight: "0px" }}
              onClick={handleClose}
            >
              {t("dialogReport.cancel")}
            </Button>
            {isEditMode && onDelete && (
              <Button
                className="btn btn-delete"
                style={{ width: "auto", marginRight: "0px" }}
                onClick={() => {
                  onDelete();
                  handleClose();
                }}
              >
                {t("formAction.delete")}
              </Button>
            )}
            <Button
              className="btn btn-primary"
              style={{ width: "auto" }}
              disabled={isSaveDisabled}
              onClick={() => {
                setTouched(true);
                if (!isSaveDisabled) {
                  handleSave();
                }
              }}
            >
              {t("formAction.add")}
            </Button>
          </>
        }
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pb: 1,
            pt: 1,
            overflow: "visible",
          }}
        >
          {masterDataError && (
            <Typography color="error" variant="body2">
              {masterDataError}
            </Typography>
          )}

          <FormControl
            fullWidth
            className="select-field"
            sx={MAINTENANCE_SELECT_FORM_CONTROL_SX}
            disabled={itemDropdownsDisabled}
            error={touched && categoryMissing}
          >
            <InputLabel id="item-editor-category-label">
              {t("dialogMaintenanceReports.category")}
            </InputLabel>
            <Select
              labelId="item-editor-category-label"
              label={t("dialogMaintenanceReports.category")}
              value={draft.categoryId}
              MenuProps={MAINTENANCE_SELECT_MENU_PROPS}
              onChange={(e: SelectChangeEvent<string>) => {
                const categoryId = e.target.value;
                const label =
                  categoryOptions.find((c) => c.id === categoryId)?.label ?? "";
                const categoryCode = getMaintenanceCategoryCode(
                  categoryId,
                  maintenanceCategories,
                );
                const hideRefillReplace =
                  shouldHideMaintenanceRefillReplaceFields(categoryCode);
                patchDraft({
                  categoryId,
                  categoryCode,
                  categoryLabel: label,
                  maintenanceTypeId: "",
                  maintenanceTypeCode: "",
                  maintenanceTypeLabel: "",
                  maintenanceItemId: "",
                  maintenanceItemLabel: "",
                  maintenanceItemOther: "",
                  action: hideRefillReplace ? "" : draft.action || "refill",
                  refillAmount: hideRefillReplace ? "" : draft.refillAmount,
                });
              }}
              renderValue={(v) => selectRenderValue(v, categoryOptions)}
            >
              {categoryOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {touched && categoryMissing && (
              <FormHelperText>{t("required")}</FormHelperText>
            )}
          </FormControl>

          {isFiltersCategory && (
            <FormControl
              fullWidth
              className="select-field"
              disabled={!draft.categoryId || itemDropdownsDisabled}
              sx={MAINTENANCE_SELECT_FORM_CONTROL_SX}
              error={touched && typeMissing}
            >
              <InputLabel id="item-editor-type-label">
                {t("subType")}
              </InputLabel>
              <Select
                labelId="item-editor-type-label"
                label={t("subType")}
                value={draft.maintenanceTypeId}
                MenuProps={MAINTENANCE_SELECT_MENU_PROPS}
                onChange={(e: SelectChangeEvent<string>) => {
                  const maintenanceTypeId = e.target.value;
                  const label =
                    maintenanceTypeOptions.find(
                      (o) => o.id === maintenanceTypeId,
                    )?.label ?? "";
                  const maintenanceTypeCode = maintenanceTypeId
                    ? getMaintenanceTypeCode(
                        maintenanceTypeId,
                        maintenanceTypes,
                      )
                    : "";
                  const isAll = isMaintenanceTypeAllChoice(
                    maintenanceTypeId,
                    maintenanceTypes,
                  );
                  const resolved =
                    maintenanceTypeId && !isAll
                      ? resolveIrregularMaintenanceItemForFiltersType(
                          draft.categoryId,
                          maintenanceTypeId,
                          irregularMaintenanceItems,
                          maintenanceTypes,
                        )
                      : null;
                  const keepOtherItem =
                    isAll ||
                    isMaintenanceTypeOtherChoice(
                      maintenanceTypeId,
                      maintenanceTypes,
                    );
                  const currentIsOther =
                    draft.maintenanceItemId === otherMaintenanceItemChoiceId;
                  patchDraft({
                    maintenanceTypeId,
                    maintenanceTypeCode,
                    maintenanceTypeLabel: label,
                    maintenanceItemId:
                      resolved?.id ??
                      (keepOtherItem && currentIsOther
                        ? draft.maintenanceItemId
                        : ""),
                    maintenanceItemLabel:
                      resolved?.label ??
                      (keepOtherItem && currentIsOther
                        ? draft.maintenanceItemLabel
                        : ""),
                    maintenanceItemOther: "",
                  });
                }}
                renderValue={(v) =>
                  selectRenderValue(v, maintenanceTypeOptions)
                }
              >
                {maintenanceTypeOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {touched && typeMissing && (
                <FormHelperText>{t("required")}</FormHelperText>
              )}
            </FormControl>
          )}

          <FormControl
            fullWidth
            className="select-field"
            disabled={
              !draft.categoryId ||
              itemDropdownsDisabled ||
              (isFiltersCategory && !draft.maintenanceTypeId)
            }
            sx={MAINTENANCE_SELECT_FORM_CONTROL_SX}
            error={touched && itemMissing}
          >
            <InputLabel id="item-editor-item-label">
              {t("dialogMaintenanceReports.item")}
            </InputLabel>
            <Select
              labelId="item-editor-item-label"
              label={t("dialogMaintenanceReports.item")}
              value={draft.maintenanceItemId}
              MenuProps={MAINTENANCE_SELECT_MENU_PROPS}
              onChange={(e: SelectChangeEvent<string>) => {
                const maintenanceItemId = e.target.value;
                const label =
                  maintenanceItemOptions.find((o) => o.id === maintenanceItemId)
                    ?.label ?? "";
                patchDraft({
                  maintenanceItemId,
                  maintenanceItemLabel: label,
                  ...(maintenanceItemId !== otherMaintenanceItemChoiceId
                    ? { maintenanceItemOther: "" }
                    : {}),
                });
              }}
              renderValue={(v) => {
                if (!v) return "";
                const selected = maintenanceItemOptions.find(
                  (opt) => opt.id === v,
                );
                if (!selected) return v;
                const Icon = getMaintenanceItemIcon(selected.code);
                return (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {Icon && (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          width: 20,
                          height: 20,
                          flexShrink: 0,
                          "& svg": { width: 20, height: 20 },
                        }}
                      >
                        <Icon />
                      </Box>
                    )}
                    <span>{selected.label}</span>
                  </Box>
                );
              }}
            >
              {maintenanceItemOptions.map((opt) => {
                const Icon = getMaintenanceItemIcon(opt.code);
                return (
                  <MenuItem
                    key={opt.id}
                    value={opt.id}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    {Icon && (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          width: 20,
                          height: 20,
                          flexShrink: 0,
                          "& svg": { width: 20, height: 20 },
                        }}
                      >
                        <Icon />
                      </Box>
                    )}
                    <span>{opt.label}</span>
                  </MenuItem>
                );
              })}
            </Select>
            {touched && itemMissing && (
              <FormHelperText>{t("required")}</FormHelperText>
            )}
          </FormControl>

          {showOtherMaintenanceItemField && (
            <TextField
              className="input-field"
              label={t("dialogMaintenanceReports.otherMaintenanceItem")}
              value={draft.maintenanceItemOther}
              fullWidth
              variant="outlined"
              required
              error={touched || otherMissing || maintenanceItemOtherError}
              helperText={
                maintenanceItemOtherError
                  ? t("invalidCharacters")
                  : touched && otherMissing
                    ? t("required")
                    : ""
              }
              onChange={(e) =>
                patchDraft({ maintenanceItemOther: e.target.value })
              }
            />
          )}

          {!hideRefillReplaceFields && (
            <>
              <FormControl component="fieldset" sx={{ mt: 0.5, width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <RadioGroup
                    row
                    value={draft.action}
                    sx={{
                      flexWrap: "nowrap",
                      gap: 6,
                      margin: 0,
                      "& .MuiFormControlLabel-root": {
                        margin: 0,
                      },
                    }}
                    onChange={(e) =>
                      patchDraft({
                        action: e.target.value as MaintenanceItemAction,
                      })
                    }
                  >
                    <FormControlLabel
                      value="refill"
                      control={<Radio sx={MAINTENANCE_ACTION_RADIO_SX} />}
                      label={t("dialogMaintenanceReports.yesRefill")}
                    />
                    <FormControlLabel
                      value="replace"
                      control={<Radio sx={MAINTENANCE_ACTION_RADIO_SX} />}
                      label={t("dialogMaintenanceReports.yesReplace")}
                    />
                  </RadioGroup>
                </Box>
                {touched && actionMissing && (
                  <Typography variant="caption" color="error">
                    {t("required")}
                  </Typography>
                )}
              </FormControl>

              <TextField
                className={`input-field ${
                  refillAmountShowError ? "error-border" : ""
                }`}
                label={t("dialogMaintenanceReports.refillAmount")}
                type="text"
                inputMode="decimal"
                fullWidth
                variant="outlined"
                value={draft.refillAmount}
                onChange={(e) => {
                  patchDraft({
                    refillAmount: sanitizeNumericInputValue(
                      e.target.value,
                      draft.refillAmount,
                    ),
                  });
                }}
                onBlur={() => {
                  const normalized = normalizeNumericOnBlur(
                    draft.refillAmount,
                    REFILL_AMOUNT_MAX,
                  );
                  if (normalized !== draft.refillAmount) {
                    patchDraft({ refillAmount: normalized });
                  }
                }}
                error={refillAmountShowError}
                helperText={refillAmountShowError ? refillAmountHelperText : ""}
                FormHelperTextProps={{
                  className: refillAmountShowError ? "error-text" : undefined,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">L</InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-input": {
                    textAlign: "right",
                    direction: "ltr",
                    paddingRight: "8px",
                  },
                }}
              />
            </>
          )}

          <div
            className="wrapper-text-field mt-8"
            style={{ position: "relative" }}
          >
            <TextField
              multiline
              rows={3}
              className="input-field"
              label={t("dialogReport.comment")}
              value={draft.comment}
              fullWidth
              onChange={(e) => {
                if (!isMaintenanceCommentWithinLimit(e.target.value)) {
                  return;
                }
                patchDraft({ comment: e.target.value });
              }}
              error={commentError}
              helperText={commentError ? t("invalidCharacters") : ""}
              InputProps={
                showRecordingControls
                  ? { style: { paddingRight: "65px" } }
                  : undefined
              }
              inputProps={{ style: { paddingRight: "20px" } }}
            />

            {showRecordingControls && (
              <div
                style={{
                  position: "absolute",
                  right: 12,
                  top: 40,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  zIndex: 10,
                  pointerEvents: "auto",
                }}
              >
                {recordingActive && (isRecording || isPaused) ? (
                  <Tooltip title={t("stop")}>
                    <StopIcon
                      onClick={stopRecording}
                      sx={{
                        color: "#d32f2f",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          color: "#a30000",
                          transform: "scale(1.1)",
                        },
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title={t("start")}>
                    <FiberManualRecordIcon
                      onClick={recordStartDisabled ? undefined : startRecording}
                      sx={{
                        color: recordStartDisabled ? "#ccc" : "#d32f2f",
                        cursor: recordStartDisabled ? "not-allowed" : "pointer",
                        pointerEvents: recordStartDisabled ? "none" : "auto",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          color: recordStartDisabled ? "#ccc" : "#a30000",
                          transform: recordStartDisabled
                            ? "none"
                            : "scale(1.1)",
                        },
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            )}
          </div>

          {isRecording && recordingActive && recordingTime < 60 && (
            <Tooltip title={t("recording")} arrow>
              <div
                className="glow-red"
                style={{
                  marginTop: "4px",
                  fontSize: "12px",
                  color: "#d32f2f",
                  backgroundColor: "#ffe6e6",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  boxShadow: "0 0 8px #f44336",
                  fontWeight: "bold",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  height: "32px",
                  width: "60px",
                }}
              >
                <FiberManualRecordIcon fontSize="small" />
                <span style={{ transform: "translateY(1px)" }}>
                  {`${recordingTime}s`}
                </span>
              </div>
            </Tooltip>
          )}

          {isPaused && recordingActive && (
            <Tooltip title={t("paused")} arrow>
              <div
                className="glow-yellow"
                style={{
                  marginTop: "4px",
                  fontSize: "12px",
                  color: "#f9a825",
                  backgroundColor: "#fff8e1",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  boxShadow: "0 0 8px #fbc02d",
                  fontWeight: "bold",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  height: "32px",
                  width: "60px",
                }}
              >
                <PauseCircleIcon fontSize="small" />
                {`${recordingTime}s`}
              </div>
            </Tooltip>
          )}

          {draft.recordedBlob && (
            <ItemBlobAudio
              blob={draft.recordedBlob}
              onRemove={handleRequestDeleteRecording}
            />
          )}

          {recordingActive && (isRecording || isPaused) && (
            <div
              className="warning flex justify-content-start"
              style={{ marginBottom: 12 }}
            >
              <IconWarningFormEdit />
              <span style={{ marginLeft: 12, flex: 1 }} className="txt-black-1">
                {t("warningUpdateItem3")}
              </span>
            </div>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <div className="update-media-upload-file mt-12 w-100">
              <MediaUpload
                key={`${ITEM_RECORDING_TARGET_ID}-${mediaPreviewNonce}-${otherItemsMediaCount}`}
                id={ITEM_RECORDING_TARGET_ID}
                setFileCount={() => {}}
                medias={draft.mediaFiles}
                setHasUploadError={setHasUploadError}
                fileUploadLimit={otherItemsMediaCount}
                showMaxUploadLimit={true}
                errMessage={
                  isMaintenanceItemsMediaTotalAtLimit(totalItemsMediaCount)
                    ? t("maxFilesExceeded", {
                        max: MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT,
                      })
                    : ""
                }
                setMedias={(files: File[]) => {
                  const maxForThisItem = Math.min(
                    MAINTENANCE_ITEM_MEDIA_LIMIT,
                    Math.max(
                      0,
                      MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT -
                        otherItemsMediaCount,
                    ),
                  );
                  patchDraft({
                    mediaFiles: files.slice(0, maxForThisItem),
                  });
                }}
                uploadDescription={uploadDescription}
                maxFiles={MAINTENANCE_ITEM_MEDIA_LIMIT}
              />
            </div>
          </Box>
        </Box>
      </DataDialog>

      <DataDialog
        title={t("errNetwork.title")}
        open={openFormNoMic}
        setOpen={setOpenFormNoMic}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("noMic")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormNoMic(false)}
              className="btn btn-primary ml-8"
            >
              {t("ok")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        title={t("deleteRecording")}
        open={openFormDeleteRecording}
        setOpen={setOpenFormDeleteRecording}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("inspectionDelete.content")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormDeleteRecording(false)}
              className="btn btn-no-border"
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              onClick={handleDeleteRecordingConfirmed}
              className="btn btn-delete"
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>
    </>
  );
}
